import { useEffect, useState } from "react";
import {
  ShieldCheck,
  FileText,
  GitBranch,
  Briefcase,
  Box,
  User,
} from "lucide-react";

const NODES = [
  {
    icon: ShieldCheck,
    label: "Threat Intel",
    sub: "live feed",
    angle: -90,
    r: 42,
  },
  {
    icon: FileText,
    label: "Scenario Gen",
    sub: "on-demand",
    angle: -30,
    r: 47,
  },
  { icon: GitBranch, label: "Topology", sub: "dynamic", angle: 30, r: 44 },
  { icon: Briefcase, label: "Org Profile", sub: "tailored", angle: 90, r: 34 },
  { icon: Box, label: "Frameworks", sub: "NIST · NICE", angle: 150, r: 46 },
  { icon: User, label: "Workforce", sub: "upskill", angle: 210, r: 40 },
];

const RADIUS = 44; // % of the ring box — the orbit the packet travels

function usePrefersReducedMotion() {
  const [reduce, setReduce] = useState(
    () =>
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches,
  );
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const on = () => setReduce(mq.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);
  return reduce;
}

function Callout({ className, dot, title, sub, delay }) {
  return (
    <div
      className={`absolute z-30 hidden sm:block ${className}`}
      style={{ animationDelay: delay }}
    >
      <div
        className="qv-card rounded-xl px-3.5 py-2.5"
        style={{
          background:
            "linear-gradient(var(--color-bg-card), var(--color-bg-card)), #141d19",
          boxShadow: "0 18px 40px -18px rgba(0,0,0,0.7)",
        }}
      >
        <div className="flex items-center gap-2">
          <span
            className="h-1.5 w-1.5 shrink-0 rounded-full"
            style={{ background: dot, boxShadow: `0 0 7px ${dot}` }}
          />
          <span className="text-[12.5px] font-semibold text-(--color-text-primary)">
            {title}
          </span>
        </div>
        <div className="mt-0.5 qv-mono text-[9px] uppercase tracking-wider text-(--color-text-muted)">
          {sub}
        </div>
      </div>
    </div>
  );
}

export default function EngineHub() {
  const reduce = usePrefersReducedMotion();
  // which node the engine is currently "touching"
  const [active, setActive] = useState(0);

  useEffect(() => {
    if (reduce) return;
    const id = setInterval(
      () => setActive((a) => (a + 1) % NODES.length),
      1400,
    );
    return () => clearInterval(id);
  }, [reduce]);

  return (
    <div className="relative mx-auto w-full max-w-150">
      {/* ambient glow */}
      <div
        className="pointer-events-none absolute -inset-10 rounded-4xl opacity-60 blur-3xl"
        style={{
          background:
            "radial-gradient(50% 50% at 50% 40%, color-mix(in srgb, var(--color-primary) 26%, transparent), transparent 72%)",
        }}
      />

      <div className="lp-float relative">
        {/* header */}
        <div className="flex items-center gap-2 px-1 pb-3 qv-mono text-[10.5px] tracking-wider">
          <span className="ml-auto flex items-center gap-1.5">
            <span className="cyber-badge-dot" />
            <span className="uppercase text-(--color-primary)">live</span>
          </span>
        </div>

        {/* hub */}
        <div className="relative">
          <div className="relative mx-auto aspect-square w-full max-w-105">
            {/* orbit rings */}
            <svg
              className="absolute inset-0 h-full w-full"
              viewBox="0 0 100 100"
              fill="none"
              aria-hidden="true"
            >
              <circle
                cx="50"
                cy="50"
                r={RADIUS}
                stroke="var(--color-border-solid)"
                strokeWidth="0.3"
              />
              <circle
                cx="50"
                cy="50"
                r="30"
                stroke="var(--color-border)"
                strokeWidth="0.3"
              />
              {/* dashed ring that slowly rotates */}
              <circle
                className={reduce ? "" : "lp-hub-ring"}
                cx="50"
                cy="50"
                r="34"
                stroke="color-mix(in srgb, var(--color-primary) 45%, transparent)"
                strokeWidth="0.3"
                strokeDasharray="1.5 3"
                style={{ transformOrigin: "50% 50%" }}
              />
              {/* spokes from core to each node, each carrying a packet out */}
              {NODES.map((n, i) => {
                const rad = (n.angle * Math.PI) / 180;
                // start just outside the core disc so packets don't emerge from
                // under it, and stop just short of the node ring
                const x1 = 50 + 15 * Math.cos(rad);
                const y1 = 50 + 15 * Math.sin(rad);
                // stop at the node circle's edge (r 5.2%) so the packet lands
                // on the node rather than blinking out short of it
                const x2 = 50 + (n.r - 5) * Math.cos(rad);
                const y2 = 50 + (n.r - 5) * Math.sin(rad);
                const spoke = `M ${x1} ${y1} L ${x2} ${y2}`;
                return (
                  <g key={n.label}>
                    <line
                      x1="50"
                      y1="50"
                      x2={50 + n.r * Math.cos(rad)}
                      y2={50 + n.r * Math.sin(rad)}
                      stroke="var(--color-border-solid)"
                      strokeWidth="0.25"
                    />
                    {!reduce && (
                      <circle
                        r="0.8"
                        fill="var(--color-primary)"
                        style={{
                          filter: "drop-shadow(0 0 2px var(--color-primary))",
                        }}
                      >
                        {/* each spoke fires on its own cycle so the hub never
                            pulses in unison */}
                        <animateMotion
                          dur={`${2.4 + i * 0.35}s`}
                          repeatCount="indefinite"
                          begin={`${i * 0.45}s`}
                          path={spoke}
                        />
                        <animate
                          attributeName="opacity"
                          values="0;1;1;0"
                          keyTimes="0;0.15;0.8;1"
                          dur={`${2.4 + i * 0.35}s`}
                          repeatCount="indefinite"
                          begin={`${i * 0.45}s`}
                        />
                      </circle>
                    )}
                  </g>
                );
              })}
              {/* packet travelling the orbit */}
              {!reduce && (
                <circle r="1" fill="var(--color-primary)">
                  <animateMotion
                    dur="9s"
                    repeatCount="indefinite"
                    path={`M ${50 + RADIUS} 50 A ${RADIUS} ${RADIUS} 0 1 1 ${
                      50 - RADIUS
                    } 50 A ${RADIUS} ${RADIUS} 0 1 1 ${50 + RADIUS} 50`}
                  />
                </circle>
              )}
            </svg>

            {/* core */}
            <div className="absolute left-1/2 top-1/2 z-20 -translate-x-1/2 -translate-y-1/2">
              <div
                className="relative grid h-26 w-26 place-items-center rounded-full text-center"
                style={{
                  background:
                    "radial-gradient(circle at 50% 35%, color-mix(in srgb, var(--color-primary) 16%, var(--color-bg-card)), var(--color-bg))",
                  border:
                    "1px solid color-mix(in srgb, var(--color-primary) 45%, transparent)",
                  boxShadow:
                    "0 0 40px -10px var(--color-shadow-primary), inset 0 0 24px -12px var(--color-primary)",
                }}
              >
                {!reduce && (
                  <span
                    aria-hidden="true"
                    className="lp-hub-pulse absolute inset-0 rounded-full"
                    style={{
                      border:
                        "1px solid color-mix(in srgb, var(--color-primary) 40%, transparent)",
                    }}
                  />
                )}
                <div>
                  <div className="qv-mono text-[7.5px] uppercase tracking-[0.16em] text-(--color-text-muted)">
                    Agentic AI
                  </div>
                  <div className="qv-mono text-[13px] font-bold uppercase tracking-wider text-(--color-text-primary)">
                    Engine
                  </div>
                  <div className="qv-mono text-[8px] text-(--color-primary)">
                    /core
                  </div>
                </div>
              </div>
            </div>

            {/* nodes */}
            {NODES.map((n, i) => {
              const Icon = n.icon;
              const rad = (n.angle * Math.PI) / 180;
              const on = i === active;
              return (
                <div
                  key={n.label}
                  className="absolute z-10 -translate-x-1/2 -translate-y-1/2"
                  style={{
                    left: `${50 + n.r * Math.cos(rad)}%`,
                    top: `${50 + n.r * Math.sin(rad)}%`,
                  }}
                >
                  <div className="flex flex-col items-center">
                    <span
                      className="grid h-11 w-11 place-items-center rounded-full transition-all duration-500"
                      style={{
                        background: on
                          ? "color-mix(in srgb, var(--color-primary) 16%, var(--color-bg-card))"
                          : "var(--color-bg-card)",
                        border: `1px solid ${
                          on
                            ? "var(--color-primary)"
                            : "color-mix(in srgb, var(--color-primary) 40%, transparent)"
                        }`,
                        boxShadow: on
                          ? "0 0 22px -4px var(--color-shadow-primary)"
                          : "none",
                      }}
                    >
                      <Icon className="h-4.5 w-4.5 text-(--color-primary)" />
                    </span>
                    <span className="mt-1.5 whitespace-nowrap qv-mono text-[8px] font-semibold uppercase tracking-wider text-(--color-text-primary)">
                      {n.label}
                    </span>
                    <span className="whitespace-nowrap qv-mono text-[7px] text-(--color-text-muted)">
                      {n.sub}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* footer stats */}
        <div className="mt-3 flex items-center justify-between px-1 qv-mono text-[9.5px] uppercase tracking-wider">
          <span className="text-(--color-text-muted)">
            Nodes <span className="text-(--color-primary)">06/06</span>
          </span>
          <span className="text-(--color-text-muted)">
            Agents <span className="text-(--color-primary)">active</span>
          </span>
          <span className="text-(--color-text-muted)">
            Uptime <span className="text-(--color-primary)">99.98%</span>
          </span>
        </div>
      </div>

      {/* Floating callouts — parked in the gutters either side of the hub, in
          the vertical gaps between nodes (top-left above Workforce, right
          between Scenario Gen and Topology, bottom-left below Frameworks) so
          they never sit on a spoke. */}
      <Callout
        className="lp-float-slow left-0 top-0 lg:-left-4"
        dot="var(--color-primary)"
        title="Agentic loop"
        sub="Always-on"
      />
      <Callout
        className="lp-float-delayed right-0 top-[46%] lg:-right-4"
        dot="#2dd4bf"
        title="Hyper-realistic"
        sub="Topology-aware"
      />
      <Callout
        className="lp-float bottom-0 left-0 lg:-left-4"
        dot="var(--color-primary)"
        title="+ 2,481 scenarios"
        sub="Generated this week"
      />
    </div>
  );
}
